import React, { useEffect, useState, useRef, useCallback } from 'react';

const Parents = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [cardsVisible, setCardsVisible] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [buttonPosition, setButtonPosition] = useState({ x: 50, y: 50 });
  const [imageScale, setImageScale] = useState(1);
  const [fadeOverlay, setFadeOverlay] = useState(true);
  const [fadeVideo, setFadeVideo] = useState(true);

  const buttonPosRef = useRef({ x: 50, y: 50 });
  const targetPosRef = useRef({ x: 50, y: 50 });
  const animationRef = useRef(null);
  const videoContainerRef = useRef(null);
  const iframeRef = useRef(null);
  const playerRef = useRef(null);

  const buttonPixelSize = 90;
  const videoId = "X6yUh1m7dv0";
  const placeholderImage =
    "https://i.postimg.cc/Kj4RPsRK/DJI-0467-MP4-00-00-06-03-Still001.jpg";
  const logoImage =
    "https://lh3.googleusercontent.com/pw/AP1GczOL48YvTNpRN2g5-JqzgLIjFDR_3iWa-Sp2uWd_jKoDV3yGmqeWM1O1GWsCJZaE7gHdSCAWKE47DgH1P27e3P_QGKUuAw696apWnjsGQUS8LkJKCmDTBOYIAWwVHdwoFefu92EYbFZXkx2mQOApmiLp=w827-h827-s-no-gm?authuser=0";

  // Fade-in observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            setTimeout(() => setCardsVisible(true), 200);
          }
        });
      },
      { threshold: 0.2 }
    );
    if (videoContainerRef.current) observer.observe(videoContainerRef.current);
    return () => observer.disconnect();
  }, []);

  // Load YouTube API
  useEffect(() => {
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }
  }, []);

  const animateButton = useCallback(() => {
    if (!videoContainerRef.current) return;
    const dx = targetPosRef.current.x - buttonPosRef.current.x;
    const dy = targetPosRef.current.y - buttonPosRef.current.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const ease = isPlaying ? 0.03 : Math.max(0.02, Math.min(0.07, distance * 0.1));

    buttonPosRef.current.x += dx * ease;
    buttonPosRef.current.y += dy * ease;

    const rect = videoContainerRef.current.getBoundingClientRect();
    const halfButtonXPercent = (buttonPixelSize / 2 / rect.width) * 100;
    const halfButtonYPercent = (buttonPixelSize / 2 / rect.height) * 100;

    buttonPosRef.current.x = Math.max(-halfButtonXPercent, Math.min(100 + halfButtonXPercent, buttonPosRef.current.x));
    buttonPosRef.current.y = Math.max(-halfButtonYPercent, Math.min(100 + halfButtonYPercent, buttonPosRef.current.y));

    setButtonPosition({ ...buttonPosRef.current });

    if (distance > 0.1) animationRef.current = requestAnimationFrame(animateButton);
  }, [isPlaying]);

  const smoothPause = useCallback(() => {
    if (!playerRef.current) return;

    let volume = playerRef.current.getVolume();
    const step = 10;
    const interval = 50;

    setFadeOverlay(true);
    setFadeVideo(true);
    setIsPlaying(false);

    targetPosRef.current = { x: 50, y: 50 };
    cancelAnimationFrame(animationRef.current);
    animationRef.current = requestAnimationFrame(animateButton);

    const fadeOut = setInterval(() => {
      volume -= step;
      if (volume <= 0) {
        volume = 0;
        playerRef.current.setVolume(volume);
        clearInterval(fadeOut);
        playerRef.current.pauseVideo();
        playerRef.current.setVolume(100);
      } else {
        playerRef.current.setVolume(volume);
      }
    }, interval);
  }, [animateButton]);

  const handleVideoPlay = (e) => {
    e.stopPropagation();
    if (!playerRef.current) return;

    setFadeOverlay(false);
    setFadeVideo(false);
    setIsPlaying(true);

    targetPosRef.current = { x: 50, y: 50 };
    cancelAnimationFrame(animationRef.current);
    animationRef.current = requestAnimationFrame(animateButton);

    playerRef.current.playVideo();
  };

  const handleMouseMove = (e) => {
    if (!videoContainerRef.current || isPlaying) return;

    const rect = videoContainerRef.current.getBoundingClientRect();
    const targetX = ((e.clientX - rect.left) / rect.width) * 100;
    const targetY = ((e.clientY - rect.top) / rect.height) * 100;

    targetPosRef.current = { x: targetX, y: targetY };
    cancelAnimationFrame(animationRef.current);
    animationRef.current = requestAnimationFrame(animateButton);

    setImageScale(1.08);
  };

  const handleMouseLeave = () => {
    targetPosRef.current = { x: 50, y: 50 };
    setTimeout(() => {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = requestAnimationFrame(animateButton);
    }, 50);
    setImageScale(1);
  };

  const initializePlayer = useCallback(() => {
    if (window.YT && iframeRef.current && !playerRef.current) {
      playerRef.current = new window.YT.Player(iframeRef.current, {
        events: {
          onReady: () => playerRef.current.setVolume(100),
          onStateChange: (event) => {
            if (event.data === window.YT.PlayerState.PAUSED || event.data === window.YT.PlayerState.ENDED) {
              smoothPause();
            }
            if (event.data === window.YT.PlayerState.PLAYING) {
              setIsPlaying(true);
              setFadeOverlay(false);
              setFadeVideo(false);
            }
          },
        },
      });
    }
  }, [smoothPause]);

  useEffect(() => {
    if (window.YT && window.YT.Player) initializePlayer();
    else window.onYouTubeIframeAPIReady = initializePlayer;
  }, [initializePlayer]);

  useEffect(() => {
    if (!videoContainerRef.current || !playerRef.current) return;

    const scrollObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting && playerRef.current && isPlaying) {
            smoothPause();
          }
        });
      },
      { threshold: 0.25 }
    );

    scrollObserver.observe(videoContainerRef.current);
    return () => scrollObserver.disconnect();
  }, [isPlaying, smoothPause]);

  useEffect(() => {
    return () => cancelAnimationFrame(animationRef.current);
  }, []);

  return (
    <section className="py-16 bg-slate-50 dark:bg-slate-900/50">
      <div className="container mx-auto px-4 max-w-screen-lg">
        {/* Heading */}
        <div
          className={`text-center mb-12 transition-all duration-1000 ease-out transform ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-red-600 drop-shadow-md">
            Welcome to Our Campus
          </h2>
          <div className="mt-2 w-24 md:w-32 h-1 bg-yellow-400 mx-auto rounded-full shadow-sm animate-pulse"></div>
          <p className="text-lg text-slate-700 dark:text-slate-400 max-w-2xl mx-auto mt-5 leading-relaxed">
            Take a virtual tour through our vibrant and welcoming campus at Anestar Victory.
          </p>
        </div>

        {/* Video Container */}
        <div
          ref={videoContainerRef}
          className={`relative w-full rounded-2xl shadow-2xl shadow-red-500/20 shadow-lg shadow-black/10 border-2 border-transparent hover:border-[#0077B6]/40 hover:scale-[1.01] transition-all duration-700 ease-out transform overflow-hidden mx-auto backdrop-blur-sm ${
            cardsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
          style={{ cursor: isPlaying ? 'default' : 'pointer', aspectRatio: '16 / 9' }}
          onClick={handleVideoPlay}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <div
            className="absolute top-0 left-0 w-full h-full pointer-events-none"
            style={{
              backgroundImage: `linear-gradient(to top, rgba(0,0,0,0.35), rgba(0,0,0,0)), url(${placeholderImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              transform: `scale(${imageScale})`,
              filter: `blur(1.5px) saturate(1.1)`,
              transition: 'transform 0.4s ease, filter 0.4s ease, opacity 0.7s ease-in-out',
              opacity: fadeOverlay ? 1 : 0,
            }}
          />
          <div
            className="absolute w-24 h-24 rounded-full flex items-center justify-center z-10 cursor-pointer overflow-hidden shadow-lg shadow-yellow-400/60 hover:scale-105 hover:rotate-2 transition-transform duration-300"
            style={{
              top: `${buttonPosition.y}%`,
              left: `${buttonPosition.x}%`,
              transform: 'translate(-50%, -50%)',
              opacity: fadeOverlay ? 1 : 0,
              transition: 'opacity 0.5s ease-in-out, transform 0.3s ease',
            }}
            onClick={handleVideoPlay}
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleVideoPlay(e)}
          >
            <img
              src={logoImage}
              alt="Play Logo"
              className={`w-full h-full object-contain ${!isPlaying ? 'animate-pulse' : ''}`}
            />
          </div>
          <iframe
            ref={iframeRef}
            className="absolute top-0 left-0 w-full h-full"
            src={`https://www.youtube.com/embed/${videoId}?enablejsapi=1&controls=1&rel=0&modestbranding=1`}
            title="Campus Tour Video"
            frameBorder="0"
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            loading="lazy"
            style={{
              opacity: fadeVideo ? 0 : 1,
              transition: 'opacity 0.7s ease-in-out',
            }}
          />
        </div>
      </div>
    </section>
  );
};

export default Parents;
